import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { FileText, Package, Users, TrendingUp, Loader2, ArrowRight, CheckCircle, XCircle, Clock, Send, Calendar } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ title, mobileTitle, value, subtext, icon: Icon, color, to }) => {
    const content = (
        <div className="p-4 sm:p-5 bg-white border border-brand-gray-200 hover:border-brand-lime/50 transition-colors h-full">
            <p className="text-xs sm:text-sm font-medium text-brand-gray-500">
                <span className="hidden sm:inline">{title}</span>
                <span className="sm:hidden">{mobileTitle || title}</span>
            </p>
            <div className="flex items-center justify-between mt-1">
                <div>
                    <p className="text-2xl sm:text-3xl font-bold font-heading text-brand-charcoal">{value}</p>
                    <p className="text-xs sm:text-sm text-brand-gray-400 mt-0.5 truncate">{subtext || '\u00A0'}</p>
                </div>
                <div className={`h-10 w-10 sm:h-12 sm:w-12 flex items-center justify-center flex-shrink-0 ${color}`}>
                    <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                </div>
            </div>
        </div>
    );
    return to ? <Link to={to}>{content}</Link> : content;
};

const AdminDashboard = () => {
    const { token, user } = useAuth();
    const [data, setData] = useState({ quotes: 0, packs: 0, customers: 0, stats: null });
    const [loading, setLoading] = useState(true);

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [quotesRes, packsRes, customersRes, statsRes] = await Promise.all([
                axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/admin/spares`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/admin/customers`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/admin/quotes/stats`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setData({
                quotes: quotesRes.data.length,
                packs: packsRes.data.length,
                customers: customersRes.data.length,
                stats: statsRes.data
            });
        } catch (error) {
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-brand-lime" /></div>;
    }

    const dashboard = data.stats?.dashboard || {};

    return (
        <div className="animate-fade-in space-y-6">
            <div>
                <h1 className="text-3xl font-bold font-heading text-brand-charcoal">Admin Dashboard</h1>
                <p className="text-brand-gray-500 mt-1">Welcome back, {user?.name}</p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <StatCard title="Total Quotes" mobileTitle="Quotes" value={data.quotes} icon={FileText} color="bg-blue-100 text-blue-600" to="/admin/quotes" />
                <StatCard title="Spares Packs" mobileTitle="Packs" value={data.packs} icon={Package} color="bg-brand-lime/20 text-brand-lime" to="/admin/spares" />
                <StatCard title="Customers" value={data.customers} icon={Users} color="bg-purple-100 text-purple-600" to="/admin/customers" />
                <StatCard
                    title="Accepted This Month"
                    mobileTitle="Accepted"
                    value={dashboard.accepted_this_month || 0}
                    subtext={`£${(dashboard.accepted_value_this_month || 0).toLocaleString('en-GB', { minimumFractionDigits: 0 })}`}
                    icon={TrendingUp}
                    color="bg-green-100 text-green-600"
                />
            </div>

            {/* Quotes Sent Overview */}
            <Card className="border-0 shadow-lg">
                <CardHeader>
                    <CardTitle className="text-xl font-bold font-heading">Quotes Activity</CardTitle>
                    <CardDescription>Number of quotes sent this period</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="p-4 bg-blue-50 border border-blue-100">
                            <div className="flex items-center gap-2 mb-2">
                                <Calendar className="h-4 w-4 text-blue-600" />
                                <span className="text-sm font-medium text-blue-800">Sent This Week</span>
                            </div>
                            <p className="text-3xl font-bold font-heading text-blue-900" data-testid="sent-this-week">{dashboard.sent_this_week || 0}</p>
                        </div>
                        <div className="p-4 bg-indigo-50 border border-indigo-100">
                            <div className="flex items-center gap-2 mb-2">
                                <Send className="h-4 w-4 text-indigo-600" />
                                <span className="text-sm font-medium text-indigo-800">Sent This Month</span>
                            </div>
                            <p className="text-3xl font-bold font-heading text-indigo-900" data-testid="sent-this-month">{dashboard.sent_this_month || 0}</p>
                        </div>
                    </div>

                    {/* Status breakdown */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-3 bg-blue-50 border border-blue-100">
                            <div className="flex items-center gap-2 mb-1">
                                <Send className="h-3.5 w-3.5 text-blue-600" />
                                <span className="text-xs font-medium text-blue-800">Sent</span>
                            </div>
                            <p className="text-xl font-bold font-heading text-blue-900">{data.stats?.sent?.count || 0}</p>
                        </div>
                        <div className="p-3 bg-green-50 border border-green-100">
                            <div className="flex items-center gap-2 mb-1">
                                <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                                <span className="text-xs font-medium text-green-800">Accepted</span>
                            </div>
                            <p className="text-xl font-bold font-heading text-green-900">{data.stats?.accepted?.count || 0}</p>
                        </div>
                        <div className="p-3 bg-red-50 border border-red-100">
                            <div className="flex items-center gap-2 mb-1">
                                <XCircle className="h-3.5 w-3.5 text-red-600" />
                                <span className="text-xs font-medium text-red-800">Declined</span>
                            </div>
                            <p className="text-xl font-bold font-heading text-red-900">{data.stats?.declined?.count || 0}</p>
                        </div>
                        <div className="p-3 bg-gray-50 border border-gray-200">
                            <div className="flex items-center gap-2 mb-1">
                                <Clock className="h-3.5 w-3.5 text-gray-500" />
                                <span className="text-xs font-medium text-gray-700">Expired</span>
                            </div>
                            <p className="text-xl font-bold font-heading text-gray-800">{data.stats?.expired?.count || 0}</p>
                        </div>
                    </div>

                    <Link to="/admin/quotes" className="flex items-center justify-center gap-2 mt-4 py-3 text-sm font-medium text-brand-lime hover:text-brand-lime/80">
                        View All Quotes <ArrowRight className="h-4 w-4" />
                    </Link>
                </CardContent>
            </Card>

            {/* Quick Links */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Link to="/admin/spares" className="p-4 bg-white border border-brand-gray-200 hover:border-brand-lime/50 transition-colors">
                    <div className="flex items-center justify-between">
                        <div><p className="font-medium text-brand-charcoal">Manage Spares</p><p className="text-sm text-brand-gray-500">Add or edit spares packs</p></div>
                        <ArrowRight className="h-5 w-5 text-brand-gray-400" />
                    </div>
                </Link>
                <Link to="/admin/customers" className="p-4 bg-white border border-brand-gray-200 hover:border-brand-lime/50 transition-colors">
                    <div className="flex items-center justify-between">
                        <div><p className="font-medium text-brand-charcoal">Manage Customers</p><p className="text-sm text-brand-gray-500">Add or edit customers</p></div>
                        <ArrowRight className="h-5 w-5 text-brand-gray-400" />
                    </div>
                </Link>
                <Link to="/admin/email-templates" className="p-4 bg-white border border-brand-gray-200 hover:border-brand-lime/50 transition-colors">
                    <div className="flex items-center justify-between">
                        <div><p className="font-medium text-brand-charcoal">Email Templates</p><p className="text-sm text-brand-gray-500">Customize quote emails</p></div>
                        <ArrowRight className="h-5 w-5 text-brand-gray-400" />
                    </div>
                </Link>
            </div>
        </div>
    );
};

export default AdminDashboard;
